/**
 * Map API / CLI failure strings to short operator-facing copy.
 * Keeps HTTP codes, nmcli/mmcli stderr, and env-var hints out of the UI.
 */

const DEVELOPER_FACING_RE =
    /(?:^|\b)(?:HTTP\s*\d{3}|\d{3}\s+(?:Unauthorized|Forbidden|Not Found|Bad Gateway|Service Unavailable)|mmcli|nmcli|dhclient|ModemManager|REACT_APP_|wifi\s+(?:rescan|list)\s+failed|no modem found|couldn't find modem|nmcli failed|stderr|stdout|status_code|FastAPI|Traceback|Exception:|Error:|ECONNREFUSED|ENOTFOUND|fetch failed|Failed to fetch|NetworkError|abort(?:ed)?|CORS|set_estop|get_stcm|set_stcm|\/validate|GRAPHQL_ERROR|Aurora\s+get_stcm|Aurora\s+reinit)(?:\b|$)/i;

const WIFI_MODULE_NOT_READY_RE =
    /(?:HTTP\s*502|502\b|wifi\s+(?:rescan|list)\s+failed|nmcli|device\s+not\s+ready|no wifi device|Bad Gateway|wi-?fi module loading)/i;

const WIFI_SPECIFIC_RE =
    /(?:wifi\s+(?:rescan|list)\s+failed|nmcli|no wifi device|wi-?fi module loading)/i;

const NO_MODEM_RE =
    /(?:no modem|couldn't find modem|mmcli\s+-L|mmcli\s+-L returned no modems)/i;

export function parseFastApiDetail(body: { detail?: unknown }): string | undefined {
    const d = body?.detail;
    if (typeof d === "string") return d;
    if (Array.isArray(d) && d[0] && typeof (d[0] as { msg?: string }).msg === "string") {
        return (d[0] as { msg: string }).msg;
    }
    return undefined;
}

function messageFromUnknown(error: unknown): string {
    if (typeof error === "string") return error.trim();
    if (error instanceof Error) return error.message.trim();
    if (error == null) return "";
    return String(error).trim();
}

export function isWifiModuleNotReadyMessage(message: string): boolean {
    return WIFI_MODULE_NOT_READY_RE.test(message.trim());
}

export function isNoMobileModemMessage(message: string): boolean {
    return NO_MODEM_RE.test(message.trim());
}

export function isDeveloperFacingMessage(message: string): boolean {
    const m = message.trim();
    if (!m) return true;
    return DEVELOPER_FACING_RE.test(m);
}

/**
 * Prefer a stable operator string when the raw message looks like tooling / HTTP noise.
 * Known domain cases (Wi‑Fi not ready, no modem) get specific copy; otherwise `fallback`.
 */
export function toUserFacingApiError(error: unknown, fallback: string): string {
    const raw = messageFromUnknown(error);
    if (!raw) return fallback;
    if (isNoMobileModemMessage(raw)) {
        return "No mobile modem detected.";
    }
    if (WIFI_SPECIFIC_RE.test(raw)) {
        return "Wi-Fi module loading…";
    }
    if (isDeveloperFacingMessage(raw)) {
        return fallback;
    }
    return raw;
}

export function messageFromFailedResponse(
    status: number,
    detail: string | undefined,
    fallback: string
): string {
    const combined = [detail?.trim(), `HTTP ${status}`].filter(Boolean).join(" — ");
    return toUserFacingApiError(combined || `HTTP ${status}`, fallback);
}

export async function throwUserFacingHttpError(res: Response, fallback: string): Promise<never> {
    let detail: string | undefined;
    try {
        const body = (await res.json()) as { detail?: unknown };
        detail = parseFastApiDetail(body);
    } catch {
        /* ignore non-JSON */
    }
    throw new Error(messageFromFailedResponse(res.status, detail, fallback));
}
